import { useState } from "react";
import "flag-icons/css/flag-icons.min.css";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface FlagOption {
  code: string; // ISO code for fi class, e.g. "de"
  label: string; // e.g. "German (de)"
}

export interface FlagSelectProps {
  value: string;
  onChange: (code: string) => void;
  options: FlagOption[];
  label?: string;
}

export function FlagSelect({
  value,
  onChange,
  options,
  label,
}: FlagSelectProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((opt) => opt.code === value);

  const handleSelect = (code: string) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-medium text-foreground">{label}</label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={label}
            className="flex items-center justify-between px-3 py-2 border border-input rounded-md bg-background cursor-pointer text-sm hover:bg-muted/40 transition-colors w-full"
          >
            <span className="flex items-center gap-2 min-w-0">
              {selectedOption ? (
                <>
                  <span
                    className={cn(
                      "fi",
                      `fi-${selectedOption.code}`,
                      "flex-shrink-0",
                    )}
                    aria-hidden="true"
                  />
                  <span className="truncate">{selectedOption.label}</span>
                </>
              ) : (
                <span className="text-muted-foreground">Select…</span>
              )}
            </span>
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground flex-shrink-0 ml-2 transition-transform duration-150",
                open && "rotate-180",
              )}
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="p-1 w-[var(--radix-popover-trigger-width)]"
          align="start"
          sideOffset={4}
        >
          <ul
            role="listbox"
            aria-label={label}
            className="max-h-60 overflow-y-auto"
          >
            {options.map((opt) => {
              const isSelected = opt.code === value;
              return (
                <li key={opt.code} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onClick={() => handleSelect(opt.code)}
                    className={cn(
                      "flex items-center gap-2 w-full px-2.5 py-1.5 rounded-sm text-sm text-left transition-colors",
                      isSelected
                        ? "bg-primary/8 font-medium text-foreground"
                        : "text-foreground hover:bg-primary/8",
                    )}
                  >
                    <span
                      className={cn("fi", `fi-${opt.code}`, "flex-shrink-0")}
                      aria-hidden="true"
                    />
                    <span className="truncate">{opt.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
